import React, { useState, useRef, useEffect } from 'react';
import { Send, Plane, Loader2, ThumbsUp, ThumbsDown, AlertCircle, User, LogOut, History, Plus, Trash2 } from 'lucide-react';
import { AuthProvider, useAuth } from './AuthContext';
import { conversationService } from './supabaseClient';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5001/api');

// Debug: log API configuration
console.log('API_BASE_URL:', API_BASE_URL);
console.log('NODE_ENV:', process.env.NODE_ENV);
console.log('REACT_APP_API_URL:', process.env.REACT_APP_API_URL);

// Main chat interface component
function ChatInterface() {
  const { user, signInWithGoogle, signOut } = useAuth();
  const [messages, setMessages] = useState([
    { 
      role: 'assistant', 
      content: 'Hi! I\'m AeroHelper, your AI flight instructor. I can help you with FAA regulations, flight procedures, ACS requirements, and pilot training questions. What would you like to know?',
      timestamp: new Date().toISOString()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversations when user is authenticated
  useEffect(() => {
    if (user) {
      loadConversations();
    }
  }, [user]);

  const loadConversations = async () => {
    try {
      const convos = await conversationService.getConversations();
      setConversations(convos);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const startNewConversation = async () => {
    if (!user) return;
    
    // Reset current chat
    setMessages([
      { 
        role: 'assistant', 
        content: 'Hi! I\'m AeroHelper, your AI flight instructor. I can help you with FAA regulations, flight procedures, ACS requirements, and pilot training questions. What would you like to know?',
        timestamp: new Date().toISOString()
      }
    ]);
    setCurrentConversationId(null);
    setShowHistory(false);
  };

  const loadConversation = async (conversationId) => {
    try {
      const messages = await conversationService.getMessages(conversationId);
      setMessages(messages);
      setCurrentConversationId(conversationId);
      setShowHistory(false);
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  };

  const deleteConversation = async (conversationId, e) => {
    // Prevent the conversation from being loaded when delete is clicked
    e.stopPropagation();
    
    try {
      const confirmed = window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.');
      if (!confirmed) return;
      
      await conversationService.deleteConversation(conversationId);
      
      // If we deleted the current conversation, reset the view
      if (currentConversationId === conversationId) {
        setCurrentConversationId(null);
        setMessages([
          { 
            role: 'assistant', 
            content: 'Hi! I\'m AeroHelper, your AI flight instructor. I can help you with FAA regulations, flight procedures, ACS requirements, and pilot training questions. What would you like to know?',
            timestamp: new Date().toISOString()
          }
        ]);
      }
      
      // Refresh the conversations list
      await loadConversations();
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      alert('Failed to delete conversation. Please try again.');
    }
  };

  const saveCurrentConversation = async (userMessage, assistantMessage) => {
    if (!user) return;

    try {
      let conversationId = currentConversationId;
      
      // Create new conversation if needed
      if (!conversationId) {
        const title = userMessage.length > 50 ? userMessage.substring(0, 47) + '...' : userMessage;
        const conversation = await conversationService.createConversation(title);
        conversationId = conversation.id;
        setCurrentConversationId(conversationId);
        
        // Save initial assistant message
        const initialMessage = messages.find(m => m.role === 'assistant');
        if (initialMessage) {
          await conversationService.saveMessage(conversationId, 'assistant', initialMessage.content, []);
        }
      }

      // Save user message
      await conversationService.saveMessage(conversationId, 'user', userMessage, []);
      
      // Save assistant message
      await conversationService.saveMessage(
        conversationId, 
        'assistant', 
        assistantMessage.response, 
        assistantMessage.sources || []
      );

      // Refresh conversations list
      await loadConversations();
    } catch (error) {
      console.error('Failed to save conversation:', error);
    }
  };

  const handleSubmit = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setError(null);
    
    const userMsg = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    
    // Build conversation history BEFORE adding the new message
    const conversationHistory = messages.map(msg => ({
      role: msg.role,
      content: msg.content
    }));
    
    // Add the current user message to the history being sent to the API
    conversationHistory.push({
      role: 'user', 
      content: userMessage
    });
    
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {

      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: userMessage,
          conversation_history: conversationHistory,
          user_id: 'demo-pilot'
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      const assistantMsg = {
        role: 'assistant',
        content: data.response,
        sources: data.sources || [],
        timestamp: data.timestamp,
        id: `msg-${Date.now()}`
      };

      setMessages(prev => [...prev, assistantMsg]);
      
      // Save conversation to database if user is logged in
      if (user) {
        await saveCurrentConversation(userMessage, data);
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Failed to get response');
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'I apologize, but I encountered an error. Please try again or consult your CFI for clarification.',
        error: true,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFeedback = async (messageId, rating) => {
    try {
      await fetch(`${API_BASE_URL}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          response_id: messageId,
          rating: rating,
          timestamp: new Date().toISOString()
        })
      });
      
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, feedbackGiven: rating }
          : msg
      ));
    } catch (err) {
      console.error('Error submitting feedback:', err);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-sky-50 to-blue-100">
      {/* Header */}
      <div className="bg-white shadow-md p-4 border-b-4 border-blue-600">
        <div className="w-full px-4 flex items-center justify-between">
          {/* Left side - Logo and Title */}
          <div className="flex items-center gap-3">
            <Plane className="text-blue-600" size={32} />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">AeroHelper</h1>
              <p className="text-sm text-gray-600">Your AI guide to pilot training and FAA regulations</p>
            </div>
          </div>
          
          {/* Right side - Controls and Authentication */}
          <div className="flex items-center">
            {user ? (
              <>
                {/* History Button */}
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 mr-16 ${
                    showHistory 
                      ? 'bg-blue-600 text-black shadow-md' 
                      : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                  }`}
                >
                  <History size={18} />
                  <span>History</span>
                </button>
                
                {/* New Chat Button */}
                <button
                  onClick={startNewConversation}
                  className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 rounded-lg font-medium transition-all duration-200 mr-16"
                >
                  <Plus size={18} />
                  <span>New Chat</span>
                </button>
                
                {/* User Info */}
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg border mr-16">
                  <User size={18} className="text-gray-600" />
                  <span className="text-sm font-medium text-gray-700 max-w-[200px] truncate">{user.email}</span>
                </div>
                
                {/* Sign Out Button */}
                <button
                  onClick={signOut}
                  className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 hover:bg-red-600 hover:text-white border border-red-200 rounded-lg font-medium transition-all duration-200 shadow-sm"
                >
                  <LogOut size={18} />
                  <span>Sign Out</span>
                </button>
              </>
            ) : (
              <button
                onClick={signInWithGoogle}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-black rounded-lg hover:bg-blue-700 border-2 border-blue-600 hover:border-blue-700 font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <User size={20} />
                <span>Sign in with Google</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="max-w-4xl mx-auto flex items-center gap-2">
            <AlertCircle className="text-red-500" size={20} />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Conversation History Sidebar */}
        {showHistory && user && (
          <div className="w-80 bg-white border-r border-gray-200 p-4 overflow-y-auto shadow-lg">
            <h3 className="font-bold text-gray-800 mb-4 text-lg border-b border-gray-200 pb-2">Conversation History</h3>
            <div className="space-y-3">
              {conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className={`relative rounded-lg transition-all duration-200 border-2 ${
                    currentConversationId === conversation.id
                      ? 'bg-blue-50 border-blue-300 shadow-md'
                      : 'hover:bg-gray-50 border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <button
                    onClick={() => loadConversation(conversation.id)}
                    className="w-full text-left p-4 pr-12 rounded-lg"
                  >
                    <div className="text-sm font-semibold text-gray-800 truncate mb-1">
                      {conversation.title}
                    </div>
                    <div className="text-xs text-gray-500 mb-1">
                      {new Date(conversation.updated_at).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-gray-400 bg-gray-100 inline-block px-2 py-1 rounded">
                      {conversation.messages?.length || 0} messages
                    </div>
                  </button>
                  
                  <button
                    onClick={(e) => deleteConversation(conversation.id, e)}
                    className="absolute top-3 right-3 p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-200 border border-transparent hover:border-red-200"
                    title="Delete conversation"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {conversations.length === 0 && (
                <p className="text-sm text-gray-500 italic">No conversations yet</p>
              )}
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="max-w-4xl mx-auto space-y-4">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-3xl rounded-2xl p-4 border-2 ${
                msg.role === 'user' 
                  ? 'bg-blue-600 text-black border-blue-700 shadow-lg' 
                  : msg.error
                  ? 'bg-red-50 border-red-300 text-gray-800'
                  : 'bg-white shadow-lg border-gray-200 text-gray-800'
              }`}>
                <p className="whitespace-pre-wrap">{msg.content}</p>
                
                {/* Sources */}
                {msg.sources && msg.sources.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 font-semibold mb-2">FAA References:</p>
                    <div className="space-y-1">
                      {msg.sources.map((source, i) => (
                        <div key={i} className="text-xs text-gray-600">
                          <span className="font-medium">{source.title}</span>
                          {source.url && (
                            <a 
                              href={source.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="ml-2 text-blue-600 hover:underline"
                            >
                              View source →
                            </a>
                          )}
                          <span className="ml-2 text-gray-400">
                            (relevance: {(source.score * 100).toFixed(0)}%)
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Feedback Buttons */}
                {msg.role === 'assistant' && msg.id && !msg.error && (
                  <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-2">
                    <span className="text-xs text-gray-500 font-medium">Was this helpful?</span>
                    <button
                      onClick={() => handleFeedback(msg.id, 5)}
                      disabled={msg.feedbackGiven}
                      className={`p-1.5 rounded-md border transition-all duration-200 ${
                        msg.feedbackGiven === 5 
                          ? 'text-green-600 bg-green-50 border-green-300' 
                          : 'text-gray-400 hover:text-green-600 hover:bg-green-50 border-gray-200 hover:border-green-300'
                      }`}
                    >
                      <ThumbsUp size={14} />
                    </button>
                    <button
                      onClick={() => handleFeedback(msg.id, 1)}
                      disabled={msg.feedbackGiven}
                      className={`p-1.5 rounded-md border transition-all duration-200 ${
                        msg.feedbackGiven === 1 
                          ? 'text-red-600 bg-red-50 border-red-300' 
                          : 'text-gray-400 hover:text-red-600 hover:bg-red-50 border-gray-200 hover:border-red-300'
                      }`}
                    >
                      <ThumbsDown size={14} />
                    </button>
                    {msg.feedbackGiven && (
                      <span className="text-xs text-gray-500 ml-2">Thank you!</span>
                    )}
                  </div>
                )}

                {/* Timestamp */}
                <p className="text-xs text-gray-400 mt-2">
                  {(() => {
                    const timestamp = msg.timestamp || msg.created_at;
                    return timestamp && !isNaN(new Date(timestamp)) 
                      ? new Date(timestamp).toLocaleTimeString() 
                      : new Date().toLocaleTimeString();
                  })()}
                </p>
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white shadow-xl rounded-2xl p-4 flex items-center gap-3 border-2 border-blue-200">
                <Loader2 className="animate-spin text-blue-600" size={24} />
                <span className="text-gray-700 font-medium">Searching FAA handbooks...</span>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>
      </div>

      {/* Input */}
      <div className="bg-white border-t-2 border-gray-200 shadow-xl p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex gap-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about regulations, procedures, ACS requirements..."
              className="flex-1 px-6 py-4 border-2 border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700 placeholder-gray-400 transition-all duration-200"
              disabled={loading}
            />
            <button
              onClick={handleSubmit}
              disabled={loading || !input.trim()}
              className="bg-blue-600 text-black p-4 rounded-2xl hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
            >
              <Send size={24} />
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-3 text-center font-medium">
            Powered by OpenAI • Information from FAA official sources
          </p>
        </div>
      </div>
    </div>
  );
}

// Main App component with authentication wrapper
function App() {
  return (
    <AuthProvider>
      <ChatInterface />
    </AuthProvider>
  );
}

export default App;
